import { useCallback } from "react";
import { chatService, type AIServiceErrorKind } from "@/services/ai";
import { buildRagContext, formatContextMessage } from "@/services/knowledge";
import { logger } from "@/services/logger";
import { useAppStore, useConversationsStore, useModelsStore, useSettingsStore } from "@/stores";
import { createId } from "@/utils";
import type { ChatImageAttachment, ChatMessage, ChatParams, Conversation, KnowledgeScope } from "@/types";

const ragLog = logger.scope("rag");

/**
 * useChat
 *
 * The one seam UI components call for anything chat-related. Wires the
 * store-agnostic `chatService` (pure streaming orchestration) into
 * `conversationsStore` (state + persistence) — neither of those two knows
 * about the other, this hook is where they meet. Nothing here renders
 * anything; it's plain orchestration logic, not a component.
 */
const FRIENDLY_ERROR: Record<AIServiceErrorKind, string> = {
  network: "Couldn't reach Ollama. Make sure it's installed and running.",
  timeout: "Ollama took too long to respond.",
  aborted: "Stopped.",
  "model-not-found": "That model isn't installed anymore. Pick another one.",
  "invalid-response": "Ollama sent back something this app couldn't read.",
  unknown: "Something went wrong talking to Ollama.",
};

function heuristicTitle(text: string): string {
  const trimmed = text.trim().replace(/\s+/g, " ");
  if (!trimmed) return "New chat";
  return trimmed.length > 48 ? `${trimmed.slice(0, 48)}…` : trimmed;
}

async function maybeGenerateTitle(conversationId: string): Promise<void> {
  const conversation = useConversationsStore.getState().byId[conversationId];
  if (!conversation || conversation.titleGenerated || !conversation.modelId) return;

  const firstUser = conversation.messages.find((m) => m.role === "user");
  if (!firstUser) return;

  const baseUrl = useSettingsStore.getState().ollamaHost;
  const generated = await chatService.generateTitle(baseUrl, conversation.modelId, firstUser.content);
  useConversationsStore.getState().renameConversation(conversationId, generated ?? heuristicTitle(firstUser.content));
}

/** Prepends a synthetic (never persisted) system message with Knowledge
 * context when the conversation has an active scope — the assistant sees
 * it for this one request only, not the stored transcript. */
async function withKnowledgeContext(
  messages: ChatMessage[],
  knowledgeScope: KnowledgeScope,
): Promise<ChatMessage[]> {
  if (knowledgeScope.mode === "none") return messages;
  const lastUser = [...messages].reverse().find((m) => m.role === "user");
  if (!lastUser) return messages;

  try {
    const settings = useSettingsStore.getState();
    const context = await buildRagContext(lastUser.content, knowledgeScope, {
      ollamaHost: settings.ollamaHost,
      embeddingModelId: settings.embeddingModelId,
    });
    if (!context) return messages;
    const contextMessage: ChatMessage = {
      id: createId("ctx"),
      role: "system",
      content: formatContextMessage(context),
      createdAt: Date.now(),
      status: "complete",
    };
    return [contextMessage, ...messages];
  } catch (err) {
    ragLog.warn("Failed to build knowledge context; sending without it.", err);
    return messages;
  }
}

async function runAssistantReply(
  conversationId: string,
  assistantMessageId: string,
  model: string,
  messages: ChatMessage[],
  params: ChatParams,
  knowledgeScope: KnowledgeScope,
): Promise<void> {
  const baseUrl = useSettingsStore.getState().ollamaHost;
  const effectiveMessages = await withKnowledgeContext(messages, knowledgeScope);
  await chatService.streamAssistantReply(
    conversationId,
    { baseUrl, model, messages: effectiveMessages, params },
    {
      onDelta: (content) => {
        useConversationsStore.getState().updateMessageContent(conversationId, assistantMessageId, content);
      },
      onDone: (content, stopped) => {
        useConversationsStore.getState().updateMessageContent(conversationId, assistantMessageId, content);
        useConversationsStore
          .getState()
          .setMessageStatus(conversationId, assistantMessageId, stopped ? "stopped" : "complete");
        if (!stopped) void maybeGenerateTitle(conversationId);
      },
      onError: (error) => {
        useConversationsStore
          .getState()
          .setMessageStatus(conversationId, assistantMessageId, "error", FRIENDLY_ERROR[error.kind]);
        useAppStore.getState().addToast({
          title: "Generation failed",
          description: FRIENDLY_ERROR[error.kind],
          variant: "error",
        });
      },
    },
  );
}

/** Appends a fresh streaming assistant placeholder and kicks off the reply
 * for whatever the conversation's messages currently are. Returns nothing —
 * callers already have what they need in the store. */
function beginAssistantReply(conversation: Conversation): void {
  if (!conversation.modelId) {
    useAppStore.getState().addToast({
      title: "No model selected",
      description: "Pick a model before sending a message.",
      variant: "warning",
    });
    return;
  }

  const assistantMessage: ChatMessage = {
    id: createId("msg"),
    role: "assistant",
    content: "",
    createdAt: Date.now(),
    status: "streaming",
  };
  useConversationsStore.getState().appendMessage(conversation.id, assistantMessage);

  const withPlaceholder = useConversationsStore.getState().byId[conversation.id];
  if (!withPlaceholder) return;

  void runAssistantReply(
    conversation.id,
    assistantMessage.id,
    conversation.modelId,
    withPlaceholder.messages.slice(0, -1),
    withPlaceholder.params,
    withPlaceholder.knowledgeScope,
  );
}

export function useChat(conversationId: string | null) {
  const conversation = useConversationsStore((s) => (conversationId ? s.byId[conversationId] : undefined));
  const isGenerating = conversation?.messages.at(-1)?.status === "streaming";

  const sendMessage = useCallback(
    (text: string, images?: ChatImageAttachment[]) => {
      const trimmed = text.trim();
      if (!trimmed && !images?.length) return;

      let conv = conversation;
      conv ??= useConversationsStore.getState().createConversation(useModelsStore.getState().activeModelId);

      const userMessage: ChatMessage = {
        id: createId("msg"),
        role: "user",
        content: trimmed,
        createdAt: Date.now(),
        status: "complete",
        images: images && images.length > 0 ? images : undefined,
      };
      useConversationsStore.getState().appendMessage(conv.id, userMessage);

      const withUserMessage = useConversationsStore.getState().byId[conv.id];
      if (withUserMessage) beginAssistantReply(withUserMessage);
    },
    [conversation],
  );

  const regenerate = useCallback(() => {
    if (!conversationId || !conversation) return;
    const lastAssistant = [...conversation.messages].reverse().find((m) => m.role === "assistant");
    if (!lastAssistant) return;

    useConversationsStore.getState().removeMessage(conversationId, lastAssistant.id);
    const withoutReply = useConversationsStore.getState().byId[conversationId];
    if (withoutReply) beginAssistantReply(withoutReply);
  }, [conversationId, conversation]);

  const editAndResend = useCallback(
    (messageId: string, text: string) => {
      if (!conversationId) return;
      const trimmed = text.trim();
      if (!trimmed) return;

      useConversationsStore.getState().editMessage(conversationId, messageId, trimmed);
      useConversationsStore.getState().removeMessagesAfter(conversationId, messageId);
      const truncated = useConversationsStore.getState().byId[conversationId];
      if (truncated) beginAssistantReply(truncated);
    },
    [conversationId],
  );

  const retry = useCallback(
    (messageId: string) => {
      if (!conversation) return;
      const message = conversation.messages.find((m) => m.id === messageId);
      if (!message) return;
      if (message.role === "assistant") {
        regenerate();
      } else {
        sendMessage(message.content);
      }
    },
    [conversation, regenerate, sendMessage],
  );

  const stop = useCallback(() => {
    if (conversationId) chatService.stop(conversationId);
  }, [conversationId]);

  return { conversation, isGenerating, sendMessage, regenerate, editAndResend, retry, stop };
}
